<template>
  <div class="product__item" style="max-width: 250px;">
    <!-- Clique na imagem com navegação programada -->
    <div @click="navegarParaProduto" class="product__image-link" style="cursor: pointer">
      <div class="product__item__img">
        <img :src="product.imagens[0]?.url || defaultImage" :alt="`Imagem do produto ${product.nome}`"
          class="img-fluid" />
      </div>
    </div>

    <!-- Botão de adicionar ao carrinho -->
    <div class="product__item__button">
      <button @click="adicionarAoCarrinho(product)"
        :class="[produtosAddCart?.includes(product.id) ? 'added' : 'add', { animating: isAnimating }]"
        :style="{ visibility: product.tem_estoque && product.tem_peso ? 'visible' : 'hidden' }">
        <i class="fas cart-icon" :class="produtosAddCart?.includes(product.id) ? 'fa-check' : 'fa-cart-plus'"></i>
        <span class="text">
          {{ produtosAddCart?.includes(product.id) ? t('productCard.added') : t('productCard.add') }}
        </span>
      </button>
    </div>

    <!-- Conteúdo clicável -->
    <div @click="navegarParaProduto" class="product__info-link" style="cursor: pointer">
      <div class="product__item__content">
        <h4 class="product__item__title">
          <span v-if="product.titulo && product.nome && product.titulo === product.nome"
            class="product__item__title--titulo">
            {{ product.titulo }}
          </span>
          <span v-else>
            <span v-if="product.titulo" class="product__item__title--titulo">{{ product.titulo }}</span>
            <span v-if="product.titulo && product.nome">&nbsp;</span>
            <span v-if="product.nome" class="product__item__title--nome">{{ product.nome }}</span>
          </span>
        </h4>

        <div class="product__item__code">
          {{ t('productCard.code') }}: {{ product.codigo }}
        </div>

        <div class="product__item__price" v-if="product.tem_estoque && formattedPrice">
          {{ formattedPrice }}
        </div>

        <small v-if="!product.tem_estoque && !product.estoque_fisico" class="tag-out-of-stock">
          {{ t('productCard.outOfStock') }}
        </small>

        <!-- Disponível apenas na loja física -->
        <small v-else-if="!product.tem_estoque && product.estoque_fisico" class="tag-in-store-only">
          {{ t('productDetail.onlyInStore') }}
        </small>

      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { eventBus } from '@/eventBus';
import defaultImage from '@/assets/images/produto-sem-imagem.png';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  currentPage: {
    type: [Number, String],
    default: 1
  }
});

const store = useStore();
const isAnimating = ref(false);

const formattedPrice = computed(() => {
  if (!props.product?.mostrar_preco) return null;
  const valor = parseFloat(props.product.valor_venda);
  const simbolo = props.product.moeda?.simbolo || '';
  const casasDecimais = props.product.moeda?.casas_decimais ?? 0;

  return `${simbolo} ${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: casasDecimais,
    maximumFractionDigits: casasDecimais
  })}`;
});

const produtosAddCart = computed(() => store.state.ecommerce.produtosAddCart || []);

const adicionarAoCarrinho = (product) => {
  if (product.tem_estoque && product.tem_peso) {
    store.commit('ecommerce/ADD_TO_CART', { ...product });
    store.commit('ecommerce/TOGGLE_CART_VISIBILITY', true);
    eventBus.emit('toggle-cart');

    isAnimating.value = true;
    setTimeout(() => {
      isAnimating.value = false;
    }, 1200);
  } else {
    if (!product.tem_estoque) {
      alert(t('outOfStock'));
    } else if (!product.tem_peso) {
      alert(t('noWeight'));
    }
  }
};


const navegarParaProduto = () => {
  localStorage.setItem('breadcrumbContext', JSON.stringify({
    categoriaSlug: route.params.categoriaSlug,
    departamentoSlug: route.params.departamentoSlug,
    brandSlug: route.params.brandSlug
  }));

  router.push({ name: 'produto', params: { slug: props.product.slug }, query: { page: props.currentPage } });
};

</script>


<style scoped>
.product__item {
  padding: 8px;
  line-height: 1.2;
  text-decoration: none;
  color: inherit;
  font-family: 'Oswald', sans-serif;
}

.product__item__img {
  width: 100%;
  margin-bottom: 0px;

}

.product__item__img img {
  max-width: 100%;
  height: auto;
  display: block;
}

.product__item__button {
  display: flex;
  justify-content: center;
  margin: 4px 0;
}


.product__item__button button {
  display: flex;
  align-items: center;
  background-color: #E23B33;
  color: white;
  border: none;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: bold;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.product__item__button button.added {
  background-color: #28a745;
}

.product__item__button button.add:hover {
  background-color: #c5312b;
}

.product__item__button button .cart-icon {
  margin-right: 6px;
}

.product__item__button button.animating {
  animation: pulse 0.6s ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.08);
  }

  100% {
    transform: scale(1);
  }
}

.product__item__content {
  padding: 0;
  margin: 0;
}

.product__item__title {
  font-size: 1.1rem;
  margin: 2px 0;
  font-weight: 600;
  line-height: 1.4;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 2.8em;
}


.product__item__title--titulo {
  font-weight: bold;
}

.product__item__title--nome {
  color: #666;
}

.product__item__code {
  font-size: 0.85rem;
  color: #a3a3a3;
  margin: 2px 0 0 0;
  margin-bottom: 10px;
}

.product__item__price {
  font-size: 2rem;
  font-weight: bold;
  color: #E23B33;
  margin: 2px 0 0 0;
  margin-bottom: 15px
}

.out-of-stock-tag {
  color: #E23B33;
  font-weight: bold;
  background-color: #f8d7da;
  padding: 2px 5px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 4px;
  font-size: 0.7rem;
}

@media (max-width: 576px) {
  .product__item__img {
    height: 120px;
  }

  .product__item__price {
    font-size: 1.4rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .product__item__title {
    font-size: 0.95rem;
    -webkit-line-clamp: 2;
  }

  .product__item__code {
    font-size: 0.7rem;
  }
}
</style>
